import { useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ToolManager } from '~/engine/tool/ToolManager';
import { Orientations } from '~/engine/view/views/PlaneView';
import {
    GridLayoutMode,
    gridLayoutModeState,
    viewportOrientationOrderState,
} from '~/state/atoms/viewerAtoms';

/**
 * Mapping from single-letter keys to the orientation that should occupy
 * the main viewport. Keys are matched case-insensitively.
 *
 * `A` → TRANSVERSAL: in radiotherapy treatment planning the plane is
 * called "axial" (Eclipse, RayStation, MIM, and DICOM all use that
 * term), so the user-facing key follows clinical convention even
 * though the internal enum is named TRANSVERSAL. `T` was rejected to
 * avoid cognitive collision with the Transform tool.
 */
const KEY_TO_ORIENTATION: Readonly<Record<string, Orientations>> = {
    a: Orientations.TRANSVERSAL_ORIENTATION,
    c: Orientations.CORONAL_ORIENTATION,
    s: Orientations.SAGITTAL_ORIENTATION,
};

/**
 * Pure logic: return a new orientation order with `target` placed at
 * index 0 (the main viewport). The remaining panes keep their relative
 * order.
 *
 * Invariants:
 *  - Output length === input length.
 *  - Output contains the same set of orientations as the input.
 *  - If `target` is not present in the input, a copy is returned unchanged
 *    (defensive — should not happen with the current `Orientations` enum).
 */
export function moveOrientationToMain(
    order: readonly Orientations[],
    target: Orientations
): Orientations[] {
    if (!order.includes(target)) {
        return [...order];
    }
    const remaining = order.filter((o) => o !== target);
    return [target, ...remaining];
}

/**
 * Hook: listen for plain A / C / S keys and put the matching plane
 * (Axial / Coronal / Sagittal) in the main viewport.
 *
 * Guards (early-return so other handlers still process the event):
 *  - Any modifier (Ctrl/Cmd/Alt/Shift) held — preserves OS/browser shortcuts
 *    such as Cmd+S (save) or Ctrl+A (select all).
 *  - GridLayoutMode is not Default (e.g. SideBySide registration mode).
 *  - The active element is an INPUT, TEXTAREA, or contentEditable element
 *    (the user is typing).
 *  - A tool action is in progress (e.g. a half-drawn line) — same idiom as
 *    SwapToMain / SwapToFullScreen.
 *  - The event is an auto-repeat (avoids accidental rapid switches when a
 *    key is held down).
 */
export function usePlaneSelectionShortcut(): void {
    const setOrder = useSetRecoilState(viewportOrientationOrderState);
    const gridLayoutMode = useRecoilValue(gridLayoutModeState);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if (
                event.ctrlKey ||
                event.metaKey ||
                event.altKey ||
                event.shiftKey
            ) {
                return;
            }
            if (event.repeat) {
                return;
            }
            const target = KEY_TO_ORIENTATION[event.key.toLowerCase()];
            if (!target) {
                return;
            }
            if (gridLayoutMode !== GridLayoutMode.Default) {
                return;
            }
            const activeElement = document.activeElement as HTMLElement | null;
            if (
                activeElement &&
                (activeElement.tagName === 'INPUT' ||
                    activeElement.tagName === 'TEXTAREA' ||
                    activeElement.isContentEditable)
            ) {
                return;
            }
            if (ToolManager.getActiveTool()?.isActionInProgress()) {
                return;
            }
            setOrder((prev) => moveOrientationToMain(prev, target));
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [gridLayoutMode, setOrder]);
}
